<template>
  <div v-clickOutside="close" class="md:relative">
    <div class="flex" @click="toggleShow">
      <slot>
        <div v-if="currentDate" class="flex items-center flex-shrink-0 border border-grey-700 px-10 py-3 text-13 rounded-full leading-tight cursor-pointer">
          <p :class="isPastDate ? 'text-error' : 'text-grey-250'">{{ computedDate }}</p>

          <i v-if="!disabled" class="icon-close text-10 opacity-50 hover:opacity-100 anim-opacity ml-10" @click.stop="dayclick(null)" />
        </div>
        <div v-else class="text-dark-600 bg-grey-500 px-10 py-4 text-13 rounded-8 leading-tight cursor-pointer">
          due date
        </div>
      </slot>
    </div>

    <div v-if="show" class="due-date pb-5 absolute z-100 rounded-10 shadow-dropdown px-5"
         :class="isDarkMode ? 'bg-sidebar' : 'bg-primary'"
    >
      <v-calendar
        :is-dark="isDarkMode"
        :attributes="computedAttributes"
        trim-weeks
        timezone="UTC"
        class="font-Inter border-none rounded-10"
        @dayclick="dayclick"
      />

      <CheckboxModule
        :model-value="duplicateToEvent"
        class="text-12 px-6"
        :disabled="hasEvent"
        label="Add this task to the week planner too"
        @update:modelValue="toggleCheckbox"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref } from 'vue'
import { useStore } from 'vuex'
import CheckboxModule from '@/components/shared/form-controls/CheckboxModule'

export default defineComponent({
  components: { CheckboxModule },
  props: {
    hasEvent: Boolean,
    currentDate: [Date, String],
    disabled: { type: Boolean, default: false }
  },

  emits: ['toggleDueDate'],

  setup (props, { emit }) {
    const { getters } = useStore()

    const duplicateToEvent = ref(props.hasEvent)
    const isDarkMode = computed(() => getters.isDarkMode)

    const show = ref(false)

    const computedDate = computed(() => {
      const month = new Date(props.currentDate).toLocaleString('en-US', {
        month: 'short',
        timeZone: 'UTC'
      })
      const day = new Date(props.currentDate).toLocaleString('en-US', {
        day: 'numeric',
        timeZone: 'UTC'
      })
      const year = new Date(props.currentDate).toLocaleString('en-US', {
        year: 'numeric',
        timeZone: 'UTC'
      })

      const currentYear = new Date().getFullYear()

      if (+currentYear === +year) {
        return `${day} ${month.toLowerCase()}`
      } else {
        return `${day} ${month.toLowerCase()} ${year}`
      }
    })

    const isPastDate = computed(() => {
      const today = new Date()
      const curDate = new Date(props.currentDate)

      return curDate.setHours(0, 0, 0, 0) < today.setHours(0, 0, 0, 0)
    })

    const classes = computed(() => {
      const prefix = show.value ? '' : 'hover:'
      return `${isDarkMode.value ? `${prefix}bg-dark-500 text-app-color` : `${prefix}bg-menu-hover text-dark-600`} rounded-6 anim-color-200`
    })

    const computedAttributes = computed(() => {
      return [
        {
          key: 'today',
          highlight: {
            contentClass: 'text-link-color anim-color',
            contentStyle: { fontWeight: 700 }
          },
          dates: new Date()
        },
        {
          key: 'currentDay',
          highlight: {
            contentClass: 'text-app-color anim-color',
            contentStyle: { fontWeight: 700, backgroundColor: isDarkMode.value ? '#333' : 'rgba(0,0,0,0.04)' }
          },
          dates: new Date(props.currentDate)
        }
      ]
    })

    function toggleShow () {
      if (props.disabled) return
      show.value = !show.value
    }

    function close () {
      show.value = false
    }

    function dayclick (value) {
      emit('toggleDueDate', {
        date: value?.id || null,
        duplicateToEvent: duplicateToEvent.value
      })
      // setTimeout(() => close(), 100)
    }

    function toggleCheckbox (value) {
      duplicateToEvent.value = value
      if (props.currentDate) {
        dayclick({ id: props.currentDate })
      }
    }

    return {
      // ref
      // data
      show,
      duplicateToEvent,
      // computed
      classes,
      computedAttributes,
      isDarkMode,
      isPastDate,
      computedDate,
      // function
      close,
      dayclick,
      toggleShow,
      toggleCheckbox
    }
  }
})
</script>

<style lang="scss">
.due-date .vc-container {
  @apply border-none;

  &.vc-is-dark {
    @apply bg-sidebar
  }
}

.vc-day-content {
  border-radius: 6px;

  &:hover {
    @apply bg-menu-hover
  }
}
.vc-day-content {
  font-weight: 400;
}

.vc-weekday {
  color: #ccc;
  font-weight: 600;
}

.vc-title {
  font-weight: 700;
  color: #323232;
}

.vc-svg-icon {
  @apply text-grey-300;

  &:hover {
    @apply text-dark-600
  }
}

.vc-arrow:hover {
  @apply bg-menu-hover
}
</style>
